import React, { useState, useEffect, useMemo } from 'react';
import {
  User,
  FileText,
  Calendar,
  AlertCircle,
  X,
  Clock,
  CheckCircle,
  XCircle,
  Eye, // <--- Added Eye icon for View button
} from 'lucide-react';
import { submitRequest, getMyRequests, cancelRequest } from '../services/requestAPIs';
import { getMyAbsences, getMyStagedAbsences } from '../services/absenceAPIs';
import { getMyLeaves } from '../services/leavesAPIs';
import { useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { reject } from 'lodash';
import RichTextEditor from './RichTextEditor';

const RequestEmployee = () => {
  const [requests, setRequests] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [pendingAbsences, setPendingAbsences] = useState([]);
  const theme = useTheme();
  const location = useLocation();

  // Form State
  const [formData, setFormData] = useState({
    type: 'Document Request',
    description: '',
    priority: 'Medium',
  });
  const [errors, setErrors] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [showOptionModal, setShowOptionModal] = useState(false);

  // View Details State (New)
  const [selectedItem, setSelectedItem] = useState(null); // <--- Stores the item currently being viewed

  // Filter & Pagination State
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const requestsPerPage = 8;
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDraft, setIsDraft] = useState(false);

  useEffect(() => {
    if (location.state?.status) {
      setFilterStatus(location.state.status.toLowerCase());
    }
    if (location.state?.filterId) {
      setSearchQuery(String(location.state.filterId));
    }
  }, [location.state]);

  const mapAbsenceType = (ty_pe) => {
    const mapping = {
      SICKNESS: 'Sick Leave',
      EARLY_DEPARTURE: 'Early Departure',
      EMERGENCY: 'Emergency Leave',
      APPOINTMENT: 'Medical Appointment',
      PERSONAL: 'Personal Leave',
    };
    return mapping[ty_pe] || 'Absence';
  };

  useEffect(() => {
    const savedDraft = localStorage.getItem('requestDraft');
    if (savedDraft) {
      const draftData = JSON.parse(savedDraft);
      setFormData(draftData);
      setIsDraft(true);
    }
  }, []);

  useEffect(() => {
    if (formData.description.trim() || formData.type !== 'Document Request') {
      localStorage.setItem('requestDraft', JSON.stringify(formData));
      setIsDraft(true);
    }
  }, [formData]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [reqData, absData, leaveData, pendingAbsences] = await Promise.all([
        getMyRequests(),
        getMyAbsences(),
        getMyLeaves(),
        getMyStagedAbsences()
      ]);
      console.log('Fetched Data:', { reqData, absData, leaveData, pendingAbsences });
      setRequests(reqData || []);
      setAbsences(absData || []);
      setLeaves(leaveData || []);
      setPendingAbsences(pendingAbsences || []);
    } catch (error) {
      console.error('Error fetching ', error);
      setError('Failed to load your requests, absences, or leaves.');
    } finally {
      setLoading(false);
    }
  };

  const buildAllRequests = (requests, absences, pendingAbsences, leaves, mapAbsenceType) => {
    const leaveTypeLabels = {
      SICK_LEAVE: 'Sick Leave',
      ANNUAL_LEAVE: 'Annual Leave',
      UNPAID_LEAVE: 'Unpaid Leave',
      MATERNITY_LEAVE: 'Maternity Leave',
      PATERNITY_LEAVE: 'Paternity Leave',
    };

    const result = [];
    (pendingAbsences || []).forEach(absence => {
      if (absence.ty_pe === 'LATENESS' || absence.absence?.ty_pe === 'LATENESS') return;
      const startDate = absence?.date || absence.absence?.date || null;
      const endDate = absence?.endDate || absence.absence?.endDate || startDate;
      result.push({
        employee: absence?.employee || {},
        type: mapAbsenceType(absence.absence?.ty_pe || absence.ty_pe),
        date: startDate,
        startDate: startDate,
        endDate: endDate,
        hours: absence?.hours || absence.absence?.hours || 0,
        description: absence?.reason || absence.absence?.reason || 'No reason provided',
        justificationFile: absence?.justificationFile || absence.absence?.justificationFile || null,
        recordedBy: absence.recordedBy ||
          (absence.employee?.firstName + " " + (absence.absence?.employee?.lastName || absence.employee?.lastName || '')),
        employeeId: absence.employee?.employeeId || 'N/A',
        id: absence?.id || absence.stageId || 'unknown',
        priority: 'Unspecified',
        status: absence.status || 'pending',
        createdAt: absence?.createdAt || startDate,
        itemType: 'absence',
        rejectionReason: absence.rejectionReason || absence.reason || null
      });
    });
    (absences || []).forEach(absence => {
      if (absence.ty_pe === 'LATENESS') return;
      const startDate = absence.date || null;
      const endDate = absence.endDate || startDate;
      result.push({
        employee: absence.employee || {},
        type: mapAbsenceType(absence.ty_pe),
        date: startDate,
        startDate: startDate,
        endDate: endDate,
        hours: absence.hours || 0,
        description: absence.reason || 'No reason provided',
        justificationFile: absence.justificationFile || null,
        recordedBy: absence.recordedBy ||
          (absence.employee?.firstName + " " + absence.employee?.lastName),
        employeeId: absence.employee?.employeeId || 'N/A',
        id: absence.id || 'unknown',
        priority: 'Unspecified',
        status: 'approved',
        createdAt: absence.createdAt || startDate,
        itemType: 'absence'
      });
    });

    (requests || []).forEach(request => {
      result.push({
        employee: request.employee || {},
        type: request.type || 'Request',
        description: request.description || 'No reason provided',
        justificationFile: request.justificationFile || null,
        recordedBy: (request.employee?.firstName + " " + request.employee?.lastName),
        employeeId: request.employee?.employeeId || 'N/A',
        id: request.requestId || 'unknown',
        priority: request.priority || 'Unspecified',
        status: request.status || 'pending',
        createdAt: request.createdAt,
        startDate: null,
        endDate: null,
        date: null,
        itemType: 'request',
        rejectionReason: request.rejectionReason || request.reason || null
      });
    });

    (leaves || []).forEach(leave => {
      if (leave.douples || leave.doubled) return;
      const typeLabel = leaveTypeLabels[leave.type] || leave.type || 'Leave Request';
      const startDate = leave.startDate || leave.date || null;
      const endDate = leave.endDate || startDate;
      result.push({
        employee: leave.employee || {},
        type: typeLabel,
        description: leave.justification || leave.description || 'No details provided',
        justificationFile: leave.justificationFile || null,
        recordedBy: leave.handledBy ||
          (leave.employee?.firstName + " " + leave.employee?.lastName),
        employeeId: leave.employee?.employeeId || 'N/A',
        id: leave.requestId || leave.id || 'unknown',
        priority: leave.priority || 'Unspecified',
        status: leave.status || 'pending',
        createdAt: leave.createdAt || leave.createdDate || startDate,
        startDate: startDate,
        endDate: endDate,
        date: startDate,
        hours: leave.hours || 0,
        itemType: 'leave',
        rejectionReason: leave.rejectionReason || leave.reason || null
      });
    });
    return result;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  };

  const validateForm = () => {
    const newErrors = {};
    const plainDescription = stripHtml(formData.description);

    if (!plainDescription.trim()) {
      newErrors.description = 'Description is required';
    } else if (plainDescription.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters long';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setLoading(true);
      const requestData = {
        type: formData.type,
        description: formData.description.trim(),
        priority: formData.priority,
      };
      const newRequest = await submitRequest(requestData);
      setRequests([newRequest, ...requests]);
      setFormData({
        type: 'Document Request',
        description: '',
        priority: 'Medium',
      });
      setShowForm(false);
      setIsDraft(false);
      localStorage.removeItem('requestDraft');
      setSuccess('Request submitted successfully!');
      await fetchData();
      setTimeout(() => setSuccess(null), 5000);
    } catch (error) {
      console.error('Submission error:', error);
      setError('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = () => {
    if (!formData.description.trim()) {
      setError('Please add a description before saving as draft.');
      return;
    }
    localStorage.setItem('requestDraft', JSON.stringify(formData));
    setIsDraft(true);
    setSuccess('Draft saved successfully!');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleClearDraft = () => {
    localStorage.removeItem('requestDraft');
    setFormData({
      type: 'Document Request',
      description: '',
      priority: 'Medium',
    });
    setIsDraft(false);
    setSuccess('Draft cleared!');
    setTimeout(() => setSuccess(null), 2000);
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this request?')) return;
    try {
      setLoading(true);
      await cancelRequest(requestId);
      setRequests(requests.map(req =>
        req.requestId === requestId ? { ...req, status: 'cancelled' } : req
      ));
      // If we are viewing the modal of the item we just cancelled, close it or update it
      if (selectedItem && selectedItem.id === requestId) {
        setSelectedItem(prev => ({ ...prev, status: 'cancelled' }));
      }
      setSuccess('Request cancelled successfully.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error cancelling request:', error);
      setError('Failed to cancel request. Please try again.');
      setTimeout(() => setError(null), 5000);
      fetchData();
    } finally {
      setLoading(false);
    }
  };

  const allItems = useMemo(() => {
    const cutoffDate = new Date('2025-11-02T00:00:00.000Z');
    const rawItems = buildAllRequests(requests, absences, pendingAbsences, leaves, mapAbsenceType);
    return rawItems
      .filter(item => {
        const d = item.createdAt || item.startDate || item.date;
        if (!d) return true;
        const itemDate = new Date(d);
        return isNaN(itemDate.getTime()) || itemDate > cutoffDate;
      })
      .sort((a, b) => {
        const timeA = new Date(a.createdAt || a.startDate || a.date || 0).getTime();
        const timeB = new Date(b.createdAt || b.startDate || b.date || 0).getTime();
        return timeB - timeA;
      });
  }, [requests, absences, pendingAbsences, leaves]);

  const availableTypes = useMemo(() => {
    const typesSet = new Set();
    allItems.forEach(item => {
      if (item.type && typeof item.type === 'string') {
        typesSet.add(item.type.trim());
      }
    });
    return Array.from(typesSet).sort((a, b) => a.localeCompare(b));
  }, [allItems]);

  const filteredItems = useMemo(() => {
    return allItems.filter(item => {
      // Type filter
      if (filterType) {
        if (filterType === 'category:leave') {
          if (item.itemType !== 'leave') return false;
        } else if (filterType === 'category:absence') {
          if (item.itemType !== 'absence') return false;
        } else if (filterType === 'category:request') {
          if (item.itemType !== 'request') return false;
        } else {
          if (
            item.type?.toLowerCase() !== filterType.toLowerCase() &&
            item.itemType?.toLowerCase() !== filterType.toLowerCase()
          ) {
            return false;
          }
        }
      }

      // Status filter
      if (filterStatus) {
        if (item.status?.toLowerCase() !== filterStatus.toLowerCase()) return false;
      }

      // Search query
      if (searchQuery && searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const typeMatch = item.type?.toLowerCase().includes(q);
        const descMatch = item.description?.toLowerCase().includes(q);
        const idMatch = String(item.id).toLowerCase().includes(q);
        const recordedByMatch = item.recordedBy?.toLowerCase().includes(q);
        if (!typeMatch && !descMatch && !idMatch && !recordedByMatch) return false;
      }

      return true;
    });
  }, [allItems, filterType, filterStatus, searchQuery]);

  const indexOfLastRequest = currentPage * requestsPerPage;
  const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstRequest, indexOfLastRequest);
  const totalPages = Math.ceil(filteredItems.length / requestsPerPage);

  const parseDateSafe = (dateString) => {
    if (!dateString) return null;
    if (typeof dateString === 'string') {
      const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const year = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1;
        const day = parseInt(match[3], 10);
        return new Date(year, month, day);
      }
    }
    return new Date(dateString);
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return '-';
    const d = parseDateSafe(dateString);
    if (!d || isNaN(d.getTime())) return String(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return <CheckCircle size={16} className="status-icon approved" />;
      case 'rejected': return <XCircle size={16} className="status-icon rejected" />;
      case 'cancelled': return <X size={16} className="status-icon cancelled" />;
      default: return <Clock size={16} className="status-icon pending" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return '#ff4757';
      case 'medium': return '#ffa502';
      case 'low': return '#26de81';
      default: return '#b2c5e6ff';
    }
  };

  // Function to open the details modal
  const handleViewItem = (item) => {
    setSelectedItem(item);
  };

  return (
    <div className={`requests-container ${theme?.palette?.mode === 'dark' ? 'dark' : ''}`}>
      {error && (
        <div className="doc-notification doc-error">
          <AlertCircle className="doc-notification-icon doc-error-icon" />
          <span className="doc-notification-text">{error}</span>
          <button onClick={() => setError(null)} className="doc-notification-close">
            <X className="doc-close-icon" />
          </button>
        </div>
      )}
      {success && (
        <div className="doc-notification doc-success">
          <CheckCircle className="doc-notification-icon doc-success-icon" />
          <span className="doc-notification-text">{success}</span>
          <button onClick={() => setSuccess(null)} className="doc-notification-close">
            <X className="doc-close-icon" />
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="request-content">
        <div className="requests-header">
          <div className="requests-title">
            <FileText size={24} />
            My Requests
          </div>
        </div>
        <div className="doc-action-bar">
          <div className="controls">
            <button
              onClick={() => {
                if (showForm) {
                  setShowForm(false);
                } else {
                  setShowOptionModal(true);
                }
              }}
              className="btn btn-primary"
              disabled={loading}
              style={{ fontFamily: 'inherit', fontSize: '14px' }}
            >
              {showForm ? 'Close Form ❌' : 'New Request ➕'}
            </button>
            {isDraft && (
              <button
                onClick={() => setShowForm(true)}
                className="btn btn-primary draft-btn"
                title="You have a saved draft"
                style={{ fontFamily: 'inherit', fontSize: '14px' }}
              >
                📝 Continue Draft
              </button>
            )}
            <div className="search-box">
              <input
                type="text"
                placeholder="Search requests... 🔍"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="filter-input"
                style={{ fontFamily: 'inherit', fontSize: '14px' }}
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
              style={{ fontFamily: 'inherit', fontSize: '14px' }}
            >
              <option value="">All Types</option>
              <optgroup label="Categories">
                <option value="category:leave">All Leave Requests</option>
                <option value="category:absence">All Absences</option>
                <option value="category:request">All General Requests</option>
              </optgroup>
              {availableTypes.length > 0 && (
                <optgroup label="Specific Types">
                  {availableTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
              style={{ fontFamily: 'inherit', fontSize: '14px' }}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
            {(filterType || filterStatus || searchQuery) && (
              <button
                onClick={() => {
                  setFilterType('');
                  setFilterStatus('');
                  setSearchQuery('');
                  setCurrentPage(1);
                }}
                className="btn btn-secondary btn-sm"
                title="Clear all filters"
                style={{
                  padding: '0.6rem 0.8rem',
                  fontSize: '13px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Clear Filters ✕
              </button>
            )}
            <div className="results-count">
              {filteredItems.length} of {allItems.length} items
            </div>
          </div>
        </div>
        <div className="request-card">
          <div className="table-container">
            {loading && allItems.length === 0 ? (
              <div className="empty-state">
                <div className="empty-content">
                  <Clock size={48} />
                  <h3>Loading...</h3>
                  <p>Loading your data...</p>
                </div>
              </div>
            ) : (
              <table className="requests-table">
                <thead>
                  <tr>
                    <th><div className="header-content"><FileText size={16} /> Type</div></th>
                    <th><div className="header-content">Description</div></th>
                    <th><div className="header-content"><Calendar size={16} /> Start & End Date</div></th>
                    <th><div className="header-content"><Clock size={16} /> Submitted</div></th>
                    <th><div className="header-content">Priority</div></th>
                    <th><div className="header-content">Status</div></th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="empty-state">
                        <div className="empty-content">
                          <FileText size={48} />
                          <h3>No items found</h3>
                          <p>{allItems.length === 0 ? "You haven't submitted any requests yet." : "Try adjusting your filters"}</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((item) => (
                      <tr key={`${item.itemType}-${item.id}`} className="request-row">
                        <td><span className="employee-name">{item.type}</span></td>
                        <td>
                          <div className="description-cell" title={stripHtml(item.description)}>
                            {stripHtml(item.description).length > 50 ? `${stripHtml(item.description).substring(0, 50)}...` : stripHtml(item.description)}
                          </div>
                        </td>
                        <td className="date-cell">
                          {item.itemType === 'leave' || item.itemType === 'absence' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                              <span><strong style={{ color: 'var(--text-secondary)' }}>Start:</strong> {formatShortDate(item.startDate || item.date)}</span>
                              <span><strong style={{ color: 'var(--text-secondary)' }}>End:</strong> {formatShortDate(item.endDate || item.startDate || item.date)}</span>
                              {Number(item.hours) > 0 && (
                                <span><strong style={{ color: 'var(--text-secondary)' }}>Hours:</strong> {item.hours} {Number(item.hours) === 1 ? 'hr' : 'hrs'}</span>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>-</span>
                          )}
                        </td>
                        <td className="date-cell">{formatDate(item.createdAt)}</td>
                        <td>
                          <span
                            className={`priority-badge ${item.priority.toLowerCase()}`}
                            style={{ backgroundColor: getPriorityColor(item.priority) }}
                          >
                            {item.priority}
                          </span>
                        </td>
                        <td>
                          <div className="status-cell">
                            {getStatusIcon(item.status)}
                            <span className={`status-badge ${item.status?.toLowerCase()}`}>
                              {item.status ? (item.status.charAt(0).toUpperCase() + item.status.slice(1)) : 'Unknown'}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="action-buttons">
                            {/* View Button - NEW */}
                            <button
                              onClick={() => handleViewItem(item)}
                              className="btn btn-primary btn-sm"
                              title="View Full Details"
                              style={{
                                backgroundColor: '#3b82f6',
                                border: 'none',
                                padding: '4px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <Eye size={14} /> View
                            </button>

                            {/* Cancel Button */}
                            {item.itemType === 'request' && item.status?.toLowerCase() === 'pending' && (
                              <button
                                onClick={() => handleCancelRequest(item.id)}
                                className="btn btn-secondary btn-sm"
                                disabled={loading}
                                title="Cancel request"
                                style={{
                                  fontFamily: 'inherit',
                                  fontSize: '14px',
                                  padding: '4px 8px',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="btn btn-secondary"
            >
              Previous ⬅️
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="btn btn-secondary"
            >
              Next ➡️
            </button>
          </div>
        )}
      </div>

      {/* --- OPTION SELECTION MODAL --- */}
      {showOptionModal && (
        <div className="form-overlay" onClick={(e) => e.target === e.currentTarget && setShowOptionModal(false)}>
          <div className="form-modal option-modal" style={{ maxWidth: '600px', padding: '2rem' }}>
            <button className="close-btn" onClick={() => setShowOptionModal(false)} aria-label="Close options">
              <X size={20} />
            </button>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '800', color: theme?.palette?.primary?.main || '#1e293b', marginBottom: '0.5rem' }}>
                What would you like to request?
              </h2>
              <p style={{ color: '#64748b', fontSize: '0.95rem' }}>
                Leave requests go to the employee leave page. All other requests remain here.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              {/* Leave Request Option */}
              <div
                className="option-card"
                onClick={() => window.location.href = '/leaves'}
                style={{
                  padding: '1.5rem',
                  borderRadius: '12px',
                  border: '2px solid #e2e8f0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: theme?.palette?.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: '1rem'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.backgroundColor = theme?.palette?.mode === 'dark' ? 'rgba(59,130,246,0.1)' : '#eff6ff';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.backgroundColor = theme?.palette?.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fff';
                }}
              >
                <div style={{ padding: '12px', borderRadius: '50%', backgroundColor: '#dcfce7', color: '#16a34a' }}>
                  <Calendar size={28} />
                </div>
                <div>
                  <h4 style={{ fontWeight: '700', marginBottom: '0.5rem', color: theme?.palette?.mode === 'dark' ? '#f8fafc' : '#1e293b' }}>
                    Leave Request
                  </h4>
                  <p style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: '1.4' }}>
                    Open the employee leave page to submit a leave request and view your leave history.
                  </p>
                </div>
              </div>

              {/* Other Request Option */}
              <div
                className="option-card"
                onClick={() => {
                  setShowForm(true);
                  setShowOptionModal(false);
                }}
                style={{
                  padding: '1.5rem',
                  borderRadius: '12px',
                  border: '2px solid #e2e8f0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: theme?.palette?.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: '1rem'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.backgroundColor = theme?.palette?.mode === 'dark' ? 'rgba(59,130,246,0.1)' : '#eff6ff';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.backgroundColor = theme?.palette?.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fff';
                }}
              >
                <div style={{ padding: '12px', borderRadius: '50%', backgroundColor: '#e0f2fe', color: '#0369a1' }}>
                  <FileText size={28} />
                </div>
                <div>
                  <h4 style={{ fontWeight: '700', marginBottom: '0.5rem', color: theme?.palette?.mode === 'dark' ? '#f8fafc' : '#1e293b' }}>
                    Other Request
                  </h4>
                  <p style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: '1.4' }}>
                    Stay on this page for documents, equipment, payroll, training or other general requests.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- CREATE NEW REQUEST FORM MODAL --- */}
      {showForm && (
        <div className="form-overlay" onClick={(e) => e.target === e.currentTarget && setShowForm(false)}>
          <div className="form-modal">
            <button className="close-btn" onClick={() => setShowForm(false)} aria-label="Close form">
              <X size={20} />
            </button>
            <div className="modal-header">
              <h3 className="form-section">
                <FileText size={20} />
                {isDraft ? 'Continue Request Draft' : 'Create New Request'}
              </h3>
              {isDraft && <span className="status-badge draft">📝 Draft saved</span>}
            </div>
            <form onSubmit={handleSubmit} className="request-form">
              <div className="form-section">
                <h3><User size={18} /> Request Details</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="type">Request Type *</label>
                    <select
                      id="type"
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="form-group select"
                    >
                      <option value="Document Request">📄 Document Request</option>
                      <option value="Equipment Request">💻 Equipment Request</option>
                      <option value="Benefits Inquiry">🏥 Benefits Inquiry</option>
                      <option value="Payroll Question">💰 Payroll Question</option>
                      <option value="Training Request">📚 Training Request</option>
                      <option value="Other">❓ Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="priority">Priority Level</label>
                    <select
                      id="priority"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      className="form-group select"
                    >
                      <option value="Low">🟢 Low</option>
                      <option value="Medium">🟡 Medium</option>
                      <option value="High">🔴 High</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="description">
                    Description * {errors.description && <AlertCircle size={14} className="error-icon" />}
                  </label>
                  <RichTextEditor
                    value={formData.description || ''}
                    onChange={(val) => setFormData({ ...formData, description: val })}
                  />
                  <div className="input-footer">
                    <span className="char-count">{stripHtml(formData.description).length}/1000 characters</span>
                    {errors.description && <span className="error">{errors.description}</span>}
                  </div>
                </div>
              </div>
              <div className="form-actions">
                <div className="draft-actions">
                  <button type="button" className="btn btn-secondary" onClick={handleSaveDraft} disabled={!stripHtml(formData.description).trim()}>
                    💾 Save Draft
                  </button>
                  {isDraft && (
                    <button type="button" className="btn btn-secondary btn-danger" onClick={handleClearDraft}>
                      🗑️ Clear Draft
                    </button>
                  )}
                </div>
                <div className="submit-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={loading || !stripHtml(formData.description).trim()}>
                    {loading ? (
                      <><span className="axo-btn-spinner" />Submitting...</>
                    ) : 'Submit Request'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- VIEW DETAILS MODAL (New Feature) --- */}
      {selectedItem && (
        <div className="form-overlay" onClick={(e) => e.target === e.currentTarget && setSelectedItem(null)}>
          <div className="form-modal detail-modal">
            <button className="close-btn" onClick={() => setSelectedItem(null)} aria-label="Close details">
              <X size={20} />
            </button>
            <div className="modal-header">
              <h3 className="form-section">
                <FileText size={20} />
                Details: {selectedItem.type}
              </h3>
              <div className="status-cell">
                {getStatusIcon(selectedItem.status)}
                <span className={`status-badge ${selectedItem.status?.toLowerCase()}`}>
                  {selectedItem.status?.toUpperCase()}
                </span>
              </div>
            </div>

            <div className="detail-content">
              <div className="detail-grid">
                <div className="detail-group">
                  <label className="detail-label">Recorded By</label>
                  <div className="detail-value">{selectedItem.recordedBy}</div>
                </div>
                <div className="detail-group">
                  <label className="detail-label">Submitted Date</label>
                  <div className="detail-value">{formatDate(selectedItem.createdAt)}</div>
                </div>
                <div className="detail-group">
                  <label className="detail-label">Priority</label>
                  <div className="detail-value">
                    <span
                      className={`priority-badge ${selectedItem.priority.toLowerCase()}`}
                      style={{ backgroundColor: getPriorityColor(selectedItem.priority) }}
                    >
                      {selectedItem.priority}
                    </span>
                  </div>
                </div>

                {/* Specific fields for Absences & Leaves */}
                {(selectedItem.itemType === 'absence' || selectedItem.itemType === 'leave' || selectedItem.startDate || selectedItem.date) && (
                  <>
                    <div className="detail-group">
                      <label className="detail-label">Start Date</label>
                      <div className="detail-value">{formatShortDate(selectedItem.startDate || selectedItem.date)}</div>
                    </div>
                    <div className="detail-group">
                      <label className="detail-label">End Date</label>
                      <div className="detail-value">
                        {formatShortDate(selectedItem.endDate || selectedItem.startDate || selectedItem.date)}
                      </div>
                    </div>
                    {selectedItem.hours > 0 && (
                      <div className="detail-group">
                        <label className="detail-label">Hours</label>
                        <div className="detail-value">{selectedItem.hours} hrs</div>
                      </div>
                    )}
                  </>
                )}

                <div className="detail-group full-width">
                  <label className="detail-label">Description / Reason</label>
                  <div
                    className="detail-value box-value"
                    style={{ lineHeight: '1.6' }}
                    dangerouslySetInnerHTML={{ __html: selectedItem.description || '' }}
                  />
                </div>
                {selectedItem.status.toLowerCase() === 'rejected' && (
                  <div className="detail-group full-width">
                    <label className="detail-label">Rejection Reason</label>
                    <div className="detail-value box-value">
                      {selectedItem.rejectionReason || selectedItem.reason || 'No rejection reason provided.'}
                    </div>
                  </div>
                )}

                {selectedItem.justificationFile && (
                  <div className="detail-group full-width">
                    <label className="detail-label">Attachment</label>
                    <div className="detail-value">
                      <span className="file-badge">📎 Justification File Attached</span>
                      {/* You can add a download link here if you have the URL */}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="form-actions" style={{ marginTop: '20px' }}>
              {selectedItem.itemType === 'request' && selectedItem.status?.toLowerCase() === 'pending' && (
                <button
                  onClick={() => handleCancelRequest(selectedItem.id)}
                  className="btn btn-danger"
                  style={{ marginRight: 'auto' }}
                >
                  Cancel Request
                </button>
              )}
              <button
                className="btn btn-primary"
                onClick={() => setSelectedItem(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        :root {
          --primary-color: #3b82f6;
          --primary-hover: #2563eb;
          --success-color: #10b981;
          --success-bg: #dcfce7;
          --danger-color: #ef4444;
          --gray-light: #f8fafc;
          --gray-dark: #64748b;
          --text-primary: #111827;
          --text-secondary: #4b5563;
          --white: #ffffff;
          --black: #000000;
          --table-bg: #fffbeb;
        }
        .dark {
          --text-primary: #f8fafc;
          --text-secondary: #94a3b8;
          --white: #1e293b;
          --gray-light: #334155;
          --gray-dark: #94a3b8;
          --table-bg: #2a2f3d;
        }
        .employee-requests {
          padding: 0;
          width: 100%;
          max-width: 100%;
          margin: 0 auto;
          background: transparent;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
          color: var(--text-primary);
        }
        .requests-container {
          padding: 0;
          width: 100%;
          max-width: 100%;
          margin: 0 auto;
          background-color: transparent;
        }
        .request-content.blurred {
          pointer-events: none;
        }
        /* --- Styles for the Detail Modal --- */
        .detail-modal {
           max-width: 700px;
        }
        .detail-grid {
           display: grid;
           grid-template-columns: 1fr 1fr;
           gap: 1.5rem;
           padding: 0.5rem;
        }
        .detail-group {
           display: flex;
           flex-direction: column;
           gap: 0.25rem;
        }
        .detail-group.full-width {
           grid-column: 1 / -1;
        }
        .detail-label {
           font-size: 0.75rem;
           text-transform: uppercase;
           letter-spacing: 0.05em;
           color: var(--text-secondary);
           font-weight: 600;
        }
        .detail-value {
           font-size: 1rem;
           color: var(--text-primary);
        }
        .box-value {
           background: var(--gray-light);
           padding: 1rem;
           border-radius: 8px;
           border: 1px solid #e2e8f0;
           min-height: 80px;
           white-space: pre-wrap;
        }
        .dark .box-value {
           background: rgba(0,0,0,0.2);
           border-color: #475569;
        }
        .file-badge {
           display: inline-flex;
           align-items: center;
           gap: 0.5rem;
           background: #e0f2fe;
           color: #0369a1;
           padding: 0.5rem 1rem;
           border-radius: 6px;
           font-size: 0.9rem;
        }
        /* --- End Detail Modal Styles --- */

        .requests-header {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-radius: 16px;
          padding: 1.5rem 2rem;
          margin-bottom: 1rem;
          box-shadow: 0 4px 12px rgba(31, 38, 135, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.18);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .dark .requests-header {
          background: rgba(15, 23, 42, 0.95);
          border-color: rgba(51, 65, 85, 0.8);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
        .btn {
          font-family: inherit;
          font-size: 14px;
          padding: 6px 12px;
          border-radius: 4px;
        }
        .btn-secondary {
          background-color: #6c757d;
          color: white;
        }
        .btn-sm {
          padding: 4px 8px;
          font-size: 14px;
        }
        .requests-title {
          display: flex;
          align-items: center;
          font-size: 1.875rem;
          gap: 0.5rem;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0 0 0.5rem 0;
          background: linear-gradient(135deg, var(--text-primary) 0%, var(--primary-color) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .controls {
          width: 100%;
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
          margin-top: 1rem;
          margin-left: 1rem;
          align-items: center;
          flex-wrap: wrap;
        }
        .search-box {
          position: static;
          flex: 1;
          min-width: 200px;
        }
        .filter-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.8);
          font-size: 0.875rem;
          transition: border-color 0.2s ease;
        }
        .dark .filter-input {
          background: rgba(51, 65, 85, 0.8);
          border-color: #475569;
          color: #e2e8f0;
        }
        .filter-input:focus {
          outline: none;
          border-color: var(--primary-color);
        }
        .filter-select,
        .form-group select {
          padding: 0.75rem 1rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.8);
          font-size: 0.875rem;
          transition: border-color 0.2s ease;
          min-width: 150px;
          color-scheme: light;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20' stroke='%2364748b' class='icon'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 10l4 4 4-4' /%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 0.75rem center;
          padding-right: 2rem;
          background-size: 1rem 1rem;
        }
        .dark .filter-select,
        .dark .form-group select {
          background: rgba(51, 65, 85, 0.8);
          color: #e2e8f0;
          border-color: #475569;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20' stroke='%2394a3b8' class='icon'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 10l4 4 4-4' /%3E%3C/svg%3E");
        }
        .results-count {
          margin-left: auto;
          font-size: 0.875rem;
          color: var(--text-secondary);
          align-self: center;
          padding: 0.60rem 0.75rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.8);
        }
        .request-card {
          background: var(--white);
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        .dark .request-card {
          background: #1e293b;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
        .table-container {
          overflow-x: auto;
        }
        .requests-table {
          width: 100%;
          border-collapse: collapse;
        }
        .requests-table th {
          background: #f1f5f9;
          color: rgb(0, 0, 0);
          padding: 0.75rem 1rem;
          text-align: left;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.025em;
          cursor: pointer;
          user-select: none;
          transition: background-color 0.2s ease;
        }
        .dark .requests-table th {
          background: #334155;
          color: #e2e8f0;
        }
        .requests-table th:hover {
          background: #e2e8f0;
        }
        .dark .requests-table th:hover {
          background: #475569;
        }
        .requests-table td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #e5e7eb;
        }
        .dark .requests-table td {
          border-bottom: 1px solid #334155;
          background-color: var(--table-bg);
        }
        .requests-table tr:hover {
          background: rgba(59, 130, 246, 0.05);
        }
        .dark .requests-table tr:hover {
          background: rgba(59, 130, 246, 0.1);
        }
        .header-content {
          display: flex;
          // align-items: center;
          gap: 0.25rem;
          // justify-content: center;
        }
        .employee-name {
          font-size: 1rem;
          font-weight: 500;
          color: var(--text-primary);
        }
        .status-badge,
        .priority-badge {
          padding: 0.375rem 0.75rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.025em;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          white-space: nowrap;
        }
        .status-badge.pending { background: #fef9c3; color: #d97706; }
        .status-badge.approved { background: #dcfce7; color: #16a34a; }
        .status-badge.rejected { background: #fee2e2; color: #991b1b; }
        .status-badge.draft { background: #fef5e7; color: #d69e2e; }
        .status-badge.cancelled { background: #f3f4f6; color: #6b7280; }
        
        .priority-badge.low { background: #d1fae5; color: #065f46; }
        .priority-badge.medium { background: #fef9c3; color: #d97706; }
        .priority-badge.high { background: #fee2e2; color: #991b1b; }
        
        .dark .status-badge,
        .dark .priority-badge { color: var(--text-primary); }
        .dark .status-badge.pending { background: #4a4120; color: #facc15; }
        .dark .status-badge.approved { background: #14532d; color: #4ade80; }
        .dark .status-badge.rejected { background: #451a1a; color: #f87171; }
        .dark .status-badge.draft { background: #4a4120; color: #facc15; }
        .dark .status-badge.cancelled { background: #374151; color: #9ca3af; }
        
        .dark .priority-badge.low { background: #0f4739; color: #34d399; }
        .dark .priority-badge.medium { background: #4a4120; color: #facc15; }
        .dark .priority-badge.high { background: #451a1a; color: #f87171; }
        
        .status-cell {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .status-icon.approved { color: #10b981; }
        .status-icon.rejected { color: #ef4444; }
        .status-icon.pending { color: #f59e0b; }
        .status-icon.cancelled { color: #6b7280; }
        .description-cell {
          line-height: 1.4;
          color: var(--text-secondary);
        }
        .date-cell {
          color: var(--text-secondary);
          font-size: 0.9rem;
        }
        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }
        .form-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(0, 0, 0, 0.15);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          animation: fadeIn 0.3s ease-out;
        }
        .form-modal {
          background: var(--white);
          border-radius: 24px;
          width: 90%;
          max-width: 1000px;
          max-height: 85vh;
          overflow-y: auto;
          position: relative;
          padding: 2rem;
          animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          border: 1px solid rgba(226, 232, 240, 0.8);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        .dark .form-modal {
          background: #1e293b;
          border-color: #334155;
          color: #f1f5f9;
        }
        .dark .form-group label,
        .dark .detail-label,
        .dark .detail-value,
        .dark .form-section {
          color: #f1f5f9;
        }
        .close-btn {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: none;
          border: none;
          font-size: 1.25rem;
          cursor: pointer;
          color: var(--gray-dark);
          padding: 0.25rem;
          border-radius: 4px;
          transition: color 0.2s ease, background-color 0.2s ease;
        }
        .dark .close-btn {
          color: #94a3b8;
        }
        .close-btn:hover {
          color: var(--text-primary);
          background: rgba(0, 0, 0, 0.1);
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e2e8f0;
        }
        .dark .modal-header {
          border-bottom-color: #334155;
        }
        .modal-header h3 {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--text-primary);
        }
        .request-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .form-section {
          margin-bottom: 1.5rem;
        }
        .form-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1rem;
        }
        .form-group {
          display: flex;
          flex-direction: column;
        }
        .form-group label {
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: var(--text-secondary);
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 0.75rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 0.875rem;
          background: var(--white);
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
          width: 100%;
        }
        .dark .form-group input,
        .dark .form-group select,
        .dark .form-group textarea {
          background: #334155;
          border-color: #475569;
          color: #e2e8f0;
        }
        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .form-group input.error,
        .form-group select.error,
        .form-group textarea.error {
          border-color: var(--danger-color);
        }
        .form-group textarea {
          resize: vertical;
          min-height: 100px;
          font-family: inherit;
        }
        .input-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 0.5rem;
        }
        .char-count {
          font-size: 0.85rem;
          color: var(--text-secondary);
        }
        .error {
          color: var(--danger-color);
          font-size: 0.75rem;
          margin-top: 0.25rem;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }
        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }
        .dark .form-actions {
          border-top-color: #475569;
        }
        .draft-actions {
          display: flex;
          gap: 0.75rem;
          margin-right: auto;
        }
        .submit-actions {
          display: flex;
          gap: 0.75rem;
        }
        .btn-primary {
          background: var(--primary-color);
          color: white;
        }
        .btn-primary:hover:not(:disabled) {
          background: var(--primary-hover);
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
        }
        .btn-secondary {
          background: var(--gray-dark);
          color: white;
        }
        .btn-secondary:hover:not(:disabled) {
          background: #4b5563;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(75, 85, 99, 0.3);
        }
        .btn-danger {
          background: var(--danger-color);
          color: white;
          border: none;
        }
        .btn-danger:hover:not(:disabled) {
          background: #dc2626;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3);
        }
        .draft-btn {
          background: #fef5e7;
          color: #d69e2e;
          border: 1px solid #f6e05e;
        }
        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .draft-btn:hover:not(:disabled) {
          background: #fed7aa;
          color: #92400e;
          transform: translateY(-1px);
        }
        .pagination {
          display: flex;
          justify-content: center;
          gap: 0.75rem;
          margin-top: 1.5rem;
          align-items: center;
        }
        .pagination .btn {
          min-width: 100px;
        }
        .pagination span {
          color: var(--text-secondary);
          font-size: 0.875rem;
          font-weight: 500;
        }
        .doc-notification {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
          position: relative;
        }
        .doc-notification.doc-error {
          background: #fee2e2;
          color: #991b1b;
        }
        .doc-notification.doc-success {
          background: #dcfce7;
          color: #16a34a;
        }
        .doc-notification-close {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 4px;
          color: inherit;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideIn {
          from { 
            transform: translateY(20px) scale(0.95); 
            opacity: 0; 
          }
          to { 
            transform: translateY(0) scale(1); 
            opacity: 1; 
          }
        }
        @media (max-width: 768px) {
          .employee-requests { padding: 0.5rem; }
          .requests-header {
            flex-direction: column;
            gap: 1rem;
            text-align: center;
          }
          .controls {
            flex-direction: column;
            gap: 0.75rem;
            margin-left: 0;
          }
          .search-box { min-width: unset; width: 100%; }
          .form-row { grid-template-columns: 1fr; }
          .form-modal {
            width: 95%;
            max-height: 95vh;
            padding: 1rem;
          }
          .requests-table { font-size: 0.75rem; }
          .requests-table th, .requests-table td { padding: 0.5rem 0.25rem; }
          .form-actions {
            flex-direction: column-reverse;
            gap: 0.5rem;
          }
          .draft-actions, .submit-actions {
            flex-direction: column;
            width: 100%;
          }
          .btn { width: 100%; justify-content: center; }
          .detail-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default RequestEmployee;

